/**
 * Unified Modal Component
 * Supports both common and admin page usage with size and footer options
 * Uses Portal to render outside of parent stacking contexts (fixes GlobalHeader backdrop-filter issue)
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: 'small' | 'medium' | 'large';
  footer?: React.ReactNode;
  desktopOnly?: boolean;
  className?: string;
  bodyClassName?: string;
}

const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  size = 'medium',
  footer,
  desktopOnly = false,
  className = '',
  bodyClassName = ''
}: ModalProps) => {
  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Use Portal to render modal at document.body level
  // This ensures the modal overlay covers the entire viewport
  // and is not affected by parent stacking contexts (e.g., GlobalHeader's backdrop-filter)
  const modalContent = (
    <div
      className={`modal-overlay ${desktopOnly ? 'modal-overlay--desktop-only' : ''} ${className}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className={`modal modal--${size}`} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="modal__header">
            <h2 className="modal__title">{title}</h2>
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="關閉"
              type="button"
            >
              ×
            </button>
          </div>
        )}
        <div className={`modal__body ${bodyClassName}`}>
          {children}
        </div>
        {footer && (
          <div className="modal__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render using Portal to body to escape any stacking context issues
  return createPortal(modalContent, document.body);
};

export default Modal;
